
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { Edit, FileText } from "lucide-react";
import { format } from "date-fns";
import { PEOQuote } from "@/types/quotes.types";
import { formatCurrency, getStatusBadge } from "../utils/quoteUtils";
import { useState, useEffect } from "react";

interface QuoteRowProps {
  quote: PEOQuote;
  onEdit: (quote: PEOQuote) => void;
}

export const QuoteRow = ({ quote, onEdit }: QuoteRowProps) => {
  const [loadStatus, setLoadStatus] = useState<string>("initial");
  
  // On mount, log detailed information about this quote to help debugging
  useEffect(() => {
    console.log(`QuoteRow mounted for quote ID: ${quote.id}`);
    console.log("Quote details:", {
      id: quote.id,
      monthly_cost: quote.monthly_cost,
      annual_cost: quote.annual_cost,
      valid_until: quote.valid_until,
      quote_file_url: quote.quote_file_url,
      company: getApplicationDisplay(),
      status: quote.status
    });
    setLoadStatus("loaded");
  }, [quote]);

  const getApplicationDisplay = () => {
    // Enhanced display logic for company names
    if (quote.application?.company_legal_name) {
      return quote.application.company_legal_name;
    }
    
    if (quote.application?.company_dba) {
      return quote.application.company_dba;
    }
    
    if (quote.company_name) {
      return quote.company_name;
    }
    
    // Use a combination of first and last name if available
    if (quote.application?.first_name || quote.application?.last_name) {
      return `${quote.application.first_name || ''} ${quote.application.last_name || ''}`.trim();
    }

    // Fall back to application ID with a friendly label
    return `Application #${quote.application_id?.substring(0, 8) || 'Unknown'}`;
  };

  const handleViewQuote = () => {
    if (quote.quote_file_url) {
      console.log('Opening quote file:', quote.quote_file_url);
      window.open(quote.quote_file_url, '_blank');
    } else {
      console.log('No quote file URL available');
    }
  };

  const handleEditClick = () => {
    console.log('Edit button clicked for quote:', quote.id);
    onEdit(quote);
  };

  // Ensure we have valid numbers before formatting
  const safeFormatCurrency = (value: any) => {
    console.log(`Formatting currency value: ${value}, type: ${typeof value}`);
    
    // If value is null, undefined or NaN
    if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
      return "—";
    }
    
    // If value is a string that can be converted to a number
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        return formatCurrency(parsed);
      }
      return "—";
    }
    
    // Value is a number
    if (typeof value === 'number') {
      return formatCurrency(value);
    }
    
    // Any other type
    return "—";
  };

  const formattedMonthlyCost = safeFormatCurrency(quote.monthly_cost);
  const formattedAnnualCost = safeFormatCurrency(quote.annual_cost);

  return (
    <TableRow key={quote.id} className="transition-colors hover:bg-muted/30">
      <TableCell>
        <div className="font-medium text-primary-dark">
          {getApplicationDisplay()}
        </div>
        {quote.application?.primary_industry && (
          <div className="text-xs text-muted-foreground mt-0.5">
            {quote.application.primary_industry}
          </div>
        )}
      </TableCell>
      <TableCell className="font-medium">
        {formattedMonthlyCost}
        {quote.monthly_cost === null && <div className="text-xs text-amber-600">No monthly cost set</div>}
      </TableCell>
      <TableCell className="font-medium">
        {formattedAnnualCost}
        {quote.annual_cost === null && <div className="text-xs text-amber-600">No annual cost set</div>}
      </TableCell>
      <TableCell>{getStatusBadge(quote.status)}</TableCell>
      <TableCell>
        {quote.valid_until ? (
          <span className="whitespace-nowrap">{format(new Date(quote.valid_until), 'MMM d, yyyy')}</span>
        ) : (
          <span className="text-amber-600 text-xs">No expiration date</span>
        )}
      </TableCell>
      <TableCell className="flex justify-end gap-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleViewQuote}
          disabled={!quote.quote_file_url}
          className="bg-white hover:bg-gray-50 border-gray-200"
          title={quote.quote_file_url ? "View quote document" : "No quote file available"}
        >
          <FileText className="h-4 w-4 mr-2 text-primary" />
          View Quote
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleEditClick}
          className="bg-white hover:bg-gray-50 border-gray-200"
        >
          <Edit className="h-4 w-4 mr-2 text-primary" />
          Edit Details
        </Button>
      </TableCell>
    </TableRow>
  );
};
